import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FiArrowLeft, FiImage, FiSend, FiUsers, FiClock, FiEdit2, FiX } from 'react-icons/fi';
import { api } from '../utils/api';
import { resizeImageToDataUrl } from '../utils/image';
import { renderMarkdown } from '../utils/markdown';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const ChatAdmin = () => {
  const { publicId } = useParams();
  const queryClient = useQueryClient();
  const [selectedGuestId, setSelectedGuestId] = React.useState(null);
  const [messageText, setMessageText] = React.useState('');
  const [imagePreview, setImagePreview] = React.useState('');
  const [isProcessingImage, setIsProcessingImage] = React.useState(false);
  const [isEditingDescription, setIsEditingDescription] = React.useState(false);
  const [descriptionDraft, setDescriptionDraft] = React.useState('');
  const [descriptionImagesDraft, setDescriptionImagesDraft] = React.useState([]);
  const [descriptionImagesTouched, setDescriptionImagesTouched] = React.useState(false);
  const [isProcessingDescriptionImages, setIsProcessingDescriptionImages] = React.useState(false);
  const [guestSort, setGuestSort] = React.useState('lastMessage');
  const [guestSortDirection, setGuestSortDirection] = React.useState('desc');
  const messagesEndRef = React.useRef(null);
  const MAX_DESCRIPTION_IMAGES = 8;

  const { data: chatData, isLoading: chatLoading } = useQuery(
    ['chat', publicId],
    async () => {
      const response = await api.get(`/chats/${publicId}`);
      return response.data.chat;
    }
  );

  React.useEffect(() => {
    if (!isEditingDescription && chatData?.description !== undefined) {
      setDescriptionDraft(chatData.description || '');
    }
  }, [chatData, isEditingDescription]);

  React.useEffect(() => {
    if (chatData?.descriptionImages !== undefined && (!isEditingDescription || !descriptionImagesTouched)) {
      setDescriptionImagesDraft(chatData.descriptionImages || []);
    }
  }, [chatData, isEditingDescription, descriptionImagesTouched]);

  const { data: guestsData = [], isLoading: guestsLoading } = useQuery(
    ['chatGuests', publicId],
    async () => {
      const response = await api.get(`/chats/${publicId}/guests`);
      return response.data.guests || [];
    }
  );

  const sortedGuests = React.useMemo(() => {
    const guests = [...guestsData];
    if (guestSort === 'firstSeen') {
      guests.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      if (guestSortDirection === 'desc') {
        guests.reverse();
      }
      return guests;
    }
    guests.sort((a, b) => {
      const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
      const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
      if (aTime !== bTime) {
        return guestSortDirection === 'desc' ? bTime - aTime : aTime - bTime;
      }
      const createdDiff = new Date(b.createdAt) - new Date(a.createdAt);
      return guestSortDirection === 'desc' ? createdDiff : -createdDiff;
    });
    return guests;
  }, [guestsData, guestSort, guestSortDirection]);

  React.useEffect(() => {
    if (!selectedGuestId && guestsData.length > 0) {
      setSelectedGuestId(guestsData[0].id);
    }
  }, [guestsData, selectedGuestId]);

  const { data: messagesData = [], isLoading: messagesLoading } = useQuery(
    ['chatMessages', publicId, selectedGuestId],
    async () => {
      const response = await api.get(`/chats/${publicId}/messages?guestId=${encodeURIComponent(selectedGuestId)}`);
      return response.data.messages || [];
    },
    {
      enabled: !!selectedGuestId
    }
  );

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messagesData]);

  const sendMessageMutation = useMutation(
    async (payload) => {
      const response = await api.post(`/chats/${publicId}/messages`, payload);
      return response.data.message;
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['chatMessages', publicId, variables.guestId]);
        queryClient.invalidateQueries(['chatGuests', publicId]);
        queryClient.invalidateQueries('chats');
      },
      onError: (error) => {
        const message = error.response?.data?.error || 'Failed to send message';
        toast.error(message);
      }
    }
  );

  const updateDescriptionMutation = useMutation(
    async (payload) => {
      const response = await api.put(`/chats/${publicId}/description`, payload);
      return response.data.chat;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['chat', publicId]);
        setIsEditingDescription(false);
        setDescriptionImagesTouched(false);
        toast.success('Description updated');
      },
      onError: (error) => {
        const message = error.response?.data?.error || 'Failed to update description';
        toast.error(message);
      }
    }
  );

  const handleImageSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    try {
      setIsProcessingImage(true);
      const dataUrl = await resizeImageToDataUrl(file, { maxDimension: 1280, quality: 0.8 });
      setImagePreview(dataUrl);
    } catch (error) {
      toast.error('Failed to process image');
    } finally {
      setIsProcessingImage(false);
    }
  };

  const handleDescriptionImageSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) {
      return;
    }
    const available = MAX_DESCRIPTION_IMAGES - descriptionImagesDraft.length;
    if (available <= 0) {
      toast.error('Maximum images reached');
      return;
    }
    const toProcess = files.slice(0, available);
    try {
      setIsProcessingDescriptionImages(true);
      const newImages = [];
      for (const file of toProcess) {
        const dataUrl = await resizeImageToDataUrl(file, { maxDimension: 1280, quality: 0.8 });
        newImages.push(dataUrl);
      }
      setDescriptionImagesDraft((prev) => [...prev, ...newImages]);
      setDescriptionImagesTouched(true);
    } catch (error) {
      toast.error('Failed to process images');
    } finally {
      setIsProcessingDescriptionImages(false);
      event.target.value = '';
    }
  };

  const handleRemoveDescriptionImage = (index) => {
    setDescriptionImagesDraft((prev) => prev.filter((_, idx) => idx !== index));
    setDescriptionImagesTouched(true);
  };

  const handleSend = async () => {
    if (!selectedGuestId) {
      toast.error('Select a guest to send a message');
      return;
    }

    if (!messageText.trim() && !imagePreview) {
      return;
    }

    try {
      if (messageText.trim()) {
        await sendMessageMutation.mutateAsync({
          guestId: selectedGuestId,
          messageType: 'text',
          content: messageText.trim()
        });
        setMessageText('');
      }

      if (imagePreview) {
        await sendMessageMutation.mutateAsync({
          guestId: selectedGuestId,
          messageType: 'image',
          content: imagePreview
        });
        setImagePreview('');
      }
    } catch (error) {
      // handled in mutation
    }
  };

  const selectedGuest = guestsData.find((guest) => guest.id === selectedGuestId);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Link to="/" className="text-gray-400 hover:text-gray-600">
                <FiArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">{chatData?.title || 'Chat Admin'}</h1>
                <p className="text-xs text-gray-500">Manage guest conversations</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {chatLoading ? (
          <div className="flex justify-center py-10">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 space-y-4">
              <div className="card">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FiUsers className="h-4 w-4 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-900">Guests</h3>
                  </div>
                  <span className="text-xs text-gray-500">{guestsData.length} total</span>
                </div>

                <div className="text-xs text-gray-500 mb-4 flex items-center gap-2">
                  <FiClock className="h-3 w-3" />
                  Created {chatData?.createdAt ? new Date(chatData.createdAt).toLocaleString() : 'Unknown'}
                </div>

                <div className="mb-4 space-y-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Sort by</label>
                  <select
                    value={guestSort}
                    onChange={(event) => setGuestSort(event.target.value)}
                    className="input-field text-sm"
                  >
                    <option value="lastMessage">Last message</option>
                    <option value="firstSeen">First seen</option>
                  </select>
                  <select
                    value={guestSortDirection}
                    onChange={(event) => setGuestSortDirection(event.target.value)}
                    className="input-field text-sm"
                  >
                    <option value="desc">DESC</option>
                    <option value="asc">ASC</option>
                  </select>
                </div>

                {guestsLoading ? (
                  <div className="flex justify-center py-6">
                    <LoadingSpinner size="sm" />
                  </div>
                ) : sortedGuests.length === 0 ? (
                  <p className="text-sm text-gray-500">No guests yet.</p>
                ) : (
                  <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
                    {sortedGuests.map((guest) => {
                      const isActive = guest.id === selectedGuestId;
                      const label = guest.displayName || guest.ip || 'Guest';
                      const lastMessage = guest.lastMessagePreview || 'No messages yet';
                      return (
                        <button
                          key={guest.id}
                          type="button"
                          onClick={() => setSelectedGuestId(guest.id)}
                          className={`w-full text-left p-3 rounded-lg border transition ${
                            isActive ? 'border-primary-300 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <p className="text-sm font-medium text-gray-900">{label}</p>
                          <p className="text-xs text-gray-500 mt-1">First seen: {new Date(guest.createdAt).toLocaleString()}</p>
                          <p className="text-xs text-gray-500 mt-1">{lastMessage}</p>
                          {guest.lastMessageAt && (
                            <p className="text-[11px] text-gray-400 mt-1">
                              Last message: {new Date(guest.lastMessageAt).toLocaleString()}
                            </p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="card mb-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-900">Chat info</h3>
                  <button
                    type="button"
                    onClick={() => {
                      setDescriptionDraft(chatData?.description || '');
                      setDescriptionImagesDraft(chatData?.descriptionImages || []);
                      setDescriptionImagesTouched(false);
                      setIsEditingDescription((prev) => !prev);
                    }}
                    className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                  >
                    <FiEdit2 className="h-3 w-3" />
                    {isEditingDescription ? 'Close' : 'Edit'}
                  </button>
                </div>

                {isEditingDescription ? (
                  <div>
                    <textarea
                      value={descriptionDraft}
                      onChange={(event) => setDescriptionDraft(event.target.value)}
                      className="input-field h-28"
                      placeholder="Write a short description in Markdown..."
                      maxLength={5000}
                    />
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <label className="btn-secondary cursor-pointer flex items-center">
                        <FiImage className="h-4 w-4 mr-2" />
                        Add images
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleDescriptionImageSelect}
                          className="hidden"
                        />
                      </label>
                      <span className="text-xs text-gray-500">
                        {descriptionImagesDraft.length}/{MAX_DESCRIPTION_IMAGES} images
                      </span>
                    </div>
                    {descriptionImagesDraft.length > 0 && (
                      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {descriptionImagesDraft.map((image, index) => (
                          <div key={`${image}-${index}`} className="relative">
                            <img src={image} alt="Description" className="h-24 w-full object-cover rounded-md border" />
                            <button
                              type="button"
                              onClick={() => handleRemoveDescriptionImage(index)}
                              className="absolute -top-2 -right-2 bg-white border border-gray-200 rounded-full p-1 text-gray-600"
                            >
                              <FiX className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-xs text-gray-500">Markdown supported.</span>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => {
                            setIsEditingDescription(false);
                            setDescriptionDraft(chatData?.description || '');
                            setDescriptionImagesDraft(chatData?.descriptionImages || []);
                            setDescriptionImagesTouched(false);
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={() => updateDescriptionMutation.mutate({
                            description: descriptionDraft,
                            descriptionImages: descriptionImagesDraft
                          })}
                          disabled={updateDescriptionMutation.isLoading || isProcessingDescriptionImages}
                        >
                          {updateDescriptionMutation.isLoading ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                    <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <p className="text-xs text-gray-500 mb-2">Preview</p>
                      <div
                        className="text-sm text-gray-700 space-y-2"
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(descriptionDraft) }}
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    {chatData?.description ? (
                      <div
                        className="text-sm text-gray-700 space-y-2"
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(chatData.description) }}
                      />
                    ) : (
                      <p className="text-sm text-gray-500">No description yet.</p>
                    )}
                    {chatData?.descriptionImages?.length > 0 && (
                      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {chatData.descriptionImages.map((image, index) => (
                          <a
                            key={`${image}-${index}`}
                            href={image}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img src={image} alt="Chat gallery" className="h-24 w-full object-cover rounded-md border" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="card h-full flex flex-col">
                <div className="border-b border-gray-200 pb-3 mb-4">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {selectedGuest ? `Chat with ${selectedGuest.displayName || selectedGuest.ip || 'Guest'}` : 'Select a guest'}
                  </h3>
                  {selectedGuest?.ip && (
                    <p className="text-xs text-gray-500">Guest IP: {selectedGuest.ip}</p>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto space-y-3 pr-2">
                  {messagesLoading ? (
                    <div className="flex justify-center py-6">
                      <LoadingSpinner size="sm" />
                    </div>
                  ) : messagesData.length === 0 ? (
                    <p className="text-sm text-gray-500">No messages yet.</p>
                  ) : (
                    messagesData.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${message.senderType === 'admin' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg px-3 py-2 text-sm ${
                            message.senderType === 'admin'
                              ? 'bg-primary-600 text-white'
                              : 'bg-gray-100 text-gray-900'
                          }`}
                        >
                          {message.messageType === 'image' ? (
                            <img src={message.content} alt="Chat upload" className="rounded-md max-h-64" />
                          ) : (
                            <p>{message.content}</p>
                          )}
                          <p className={`mt-1 text-[10px] ${message.senderType === 'admin' ? 'text-white/70' : 'text-gray-500'}`}>
                            {message.senderName || (message.senderType === 'admin' ? 'Admin' : 'Guest')} -{' '}
                            {new Date(message.createdAt).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <div className="mt-4 border-t border-gray-200 pt-4">
                  {imagePreview && (
                    <div className="mb-3">
                      <div className="relative inline-block">
                        <img src={imagePreview} alt="Preview" className="h-24 w-32 object-cover rounded-md border" />
                        <button
                          type="button"
                          onClick={() => setImagePreview('')}
                          className="absolute -top-2 -right-2 bg-white border border-gray-200 rounded-full p-1 text-gray-600"
                        >
                          x
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-3">
                    <label className="btn-secondary cursor-pointer flex items-center">
                      <FiImage className="h-4 w-4 mr-2" />
                      Image
                      <input type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
                    </label>
                    <input
                      value={messageText}
                      onChange={(event) => setMessageText(event.target.value)}
                      placeholder="Type a message..."
                      className="input-field flex-1"
                    />
                    <button
                      type="button"
                      onClick={handleSend}
                      disabled={sendMessageMutation.isLoading || isProcessingImage}
                      className="btn-primary flex items-center"
                    >
                      <FiSend className="h-4 w-4 mr-2" />
                      Send
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatAdmin;
